/**
 * Ask a Friend - shared poll-creation helper.
 *
 * Used by both the compare drawer (assets/compare-drawer.js) and the
 * single-product "Ask a Friend" button (sections/main-product.liquid).
 *
 * Flow:
 *   1. Review step - shopper sees every item in the poll with a thumbnail,
 *      price, and a "Remove" checkbox per item, plus a "show prices"
 *      toggle and an optional notify-by-email field.
 *   2. Ready step - once they submit, the same modal is swapped in place
 *      to show a preview of exactly what their friend will see (products,
 *      prices or not, vote buttons), followed by the finished, shareable
 *      poll link (copy / email / text / view results), built entirely
 *      client-side - nothing is stored to create a poll, and no new tab
 *      is ever opened, so this can't be blocked by a popup blocker.
 *
 * Two flavors of the same poll link are produced: the one that gets
 * copied/emailed/texted is the plain voting link a friend opens, while
 * "View results" carries an extra `aaf_view=results` marker so the poll
 * page (templates/page.ask-a-friend.liquid) can recognize the creator
 * checking back in and show a read-only tally instead of asking them to
 * vote on their own poll. Clicking "View results" itself, though, never
 * navigates away - it opens the same tally in a large in-page modal (see
 * openResultsPanel below), so the shopper never lands on a bare page with
 * no way back. The `aaf_view=results` link is still built and handed to
 * "Copy results link", for a shopper who wants to save/share it and check
 * back later outside of this session.
 *
 * NOTE: this used to be named `view=results`, but `view` is a reserved
 * Shopify query parameter - appending `?view=<value>` to any storefront
 * URL tells Shopify to try rendering that resource with an alternate
 * template named `<resource>.<value>.liquid`, completely independent of
 * whatever template the resource actually has assigned. Since no
 * `templates/page.results.liquid` exists, Shopify was silently falling
 * back to the theme's generic default page template any time this link
 * was opened - the poll UI never even loaded. Renamed to the
 * app-namespaced `aaf_view` to avoid colliding with Shopify's own
 * reserved parameter.
 *
 * If a backend URL is configured (settings.ask_a_friend_endpoint), a
 * "poll created" event is also logged so usage can be tracked, and the
 * optional notify email is carried along so a vote-received email can be
 * sent later.
 */
(function () {
  var MAX_PREFILL_LENGTH = 6500;

  function generatePollId() {
    return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
  }

  function encodePrefill(data) {
    var b64 = btoa(unescape(encodeURIComponent(JSON.stringify(data))));

    // The poll link carries all of this as one URL. Photos are usually
    // small enough to fit, but if they aren't (long titles, several
    // options), drop the photos rather than let the whole link - including
    // the item names and prices - fail to load.
    if (encodeURIComponent(b64).length > MAX_PREFILL_LENGTH) {
      var textOnly = Object.assign({}, data, {
        options: data.options.map(function (option) {
          return Object.assign({}, option, { image: '' });
        })
      });
      b64 = btoa(unescape(encodeURIComponent(JSON.stringify(textOnly))));
    }

    return b64;
  }

  function logEvent(payload) {
    var settings = window.askAFriendSettings || {};
    if (!settings.endpoint) return;
    try {
      // Fire-and-forget: usage logging should never block or break the
      // shopper's experience if the backend is slow, misconfigured, or down.
      fetch(settings.endpoint, { method: 'POST', body: JSON.stringify(payload) }).catch(function () {});
    } catch (e) {
      // Ignore
    }
  }

  /**
   * Translated strings come from Shopify's `t` filter, which HTML-escapes
   * plain-text translations (e.g. an apostrophe becomes `&#39;`) so they're
   * safe to drop into HTML markup. We instead drop them into a JS string
   * via `| json`, where that escaping is never undone - so without this,
   * "We'll" would literally show up on screen as "We&#39;ll". This runs
   * any translated string back through the browser's own HTML parser to
   * turn entities back into normal characters before we use them.
   */
  function decodeHtmlEntities(str) {
    if (!str) return str;
    var el = document.createElement('textarea');
    el.innerHTML = str;
    return el.value;
  }

  function pick(value, fallback) {
    var decoded = value ? decodeHtmlEntities(value) : '';
    return decoded || fallback;
  }

  function strings() {
    var s = window.askAFriendStrings || {};
    return {
      reviewTitle: pick(s.reviewTitle, 'Review your poll'),
      showPricesLabel: pick(s.showPricesLabel, 'Show prices to your friend'),
      removeLabel: pick(s.removeLabel, 'Remove'),
      keepOneMessage: pick(s.keepOneMessage, 'Keep at least one item in your poll.'),
      emailLabel: pick(s.emailLabel, 'Email (optional)'),
      emailPlaceholder: pick(s.emailPlaceholder, 'you@example.com'),
      emailNote: pick(s.emailNote, "We'll only use this to notify you about votes on this poll - never for anything else."),
      createButton: pick(s.createButton, 'Create poll link'),
      previewLabel: pick(s.previewLabel, 'This is what your friend will see:'),
      voteButtonLabel: pick(s.voteButtonLabel, 'Vote for this'),
      readyTitle: pick(s.readyTitle, 'Your poll is ready to be shared'),
      copyLink: pick(s.copyLink, 'Copy link*'),
      copyLinkNote: pick(s.copyLinkNote, "*Paste this into a text or email to share with a friend, family member, or colleague. We don't link directly, to protect your privacy."),
      emailShare: pick(s.emailShare, 'Email a friend'),
      textShare: pick(s.textShare, 'Text a friend'),
      viewPoll: pick(s.viewPoll, 'Check results later'),
      copyResultsLink: pick(s.copyResultsLink, 'Copy results link'),
      checkResultsNote: pick(s.checkResultsNote, 'Nothing to see yet - copy this link to check back once votes start coming in.'),
      resultsTitle: pick(s.resultsTitle, 'Poll results'),
      resultsEmptyNote: pick(s.resultsEmptyNote, "Your friends haven't voted yet. Check back later."),
      resultsLoadErrorNote: pick(s.resultsLoadErrorNote, "Couldn't load results right now - try again in a moment."),
      loading: pick(s.loading, 'Loading…')
    };
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str == null ? '' : String(str);
    return div.innerHTML;
  }

  function buildOverlay() {
    var overlay = document.createElement('div');
    overlay.className = 'ask-a-friend-overlay';
    overlay.setAttribute(
      'style',
      'position:fixed;inset:0;background:rgba(0,0,0,0.5);z-index:99999;display:flex;align-items:center;justify-content:center;padding:16px;'
    );

    var box = document.createElement('div');
    box.className = 'ask-a-friend-box';
    box.setAttribute(
      'style',
      'background:#fff;color:#222;border-radius:8px;max-width:500px;width:100%;padding:28px;font-family:inherit;box-shadow:0 10px 40px rgba(0,0,0,0.25);max-height:calc(100vh - 32px);overflow-y:auto;'
    );

    overlay.appendChild(box);

    // Clicking the backdrop dismisses whichever step is showing. On the
    // review step nothing has been created yet, so this simply cancels;
    // on the ready step the poll link already exists, so this just closes
    // the panel.
    overlay.addEventListener('click', function (evt) {
      if (evt.target === overlay) overlay.remove();
    });

    return { overlay: overlay, box: box };
  }

  function renderReviewPanel(box, pollData, t, onDone) {
    var items = pollData.options.map(function (option) {
      return { option: option };
    });

    box.innerHTML =
      '<h3 style="margin:0 0 16px;font-size:1.3em;line-height:1.3;">' + escapeHtml(t.reviewTitle) + '</h3>' +
      '<div class="ask-a-friend-items" style="display:flex;flex-direction:column;gap:12px;margin-bottom:16px;"></div>' +
      '<label style="display:flex;align-items:center;gap:8px;font-size:1.05em;margin-bottom:12px;cursor:pointer;">' +
        '<input type="checkbox" class="ask-a-friend-show-prices" checked style="margin:0;">' + escapeHtml(t.showPricesLabel) +
      '</label>' +
      '<p class="ask-a-friend-error" style="display:none;color:#c0392b;font-size:1em;margin:0 0 12px;"></p>' +
      '<hr style="border:none;border-top:1px solid #e5e5e5;margin:0 0 18px;">' +
      '<label style="display:block;font-size:1em;margin-bottom:5px;">' + escapeHtml(t.emailLabel) + '</label>' +
      '<input type="email" class="ask-a-friend-email-input" placeholder="' + escapeHtml(t.emailPlaceholder) + '" ' +
        'style="width:100%;box-sizing:border-box;padding:12px;border:1px solid #ccc;border-radius:4px;font-size:1.1em;">' +
      '<p style="margin:7px 0 18px;font-size:0.94em;opacity:0.65;line-height:1.4;">' + escapeHtml(t.emailNote) + '</p>' +
      '<button type="button" class="btn btn--primary ask-a-friend-submit" style="width:100%;">' + escapeHtml(t.createButton) + '</button>';

    var itemsEl = box.querySelector('.ask-a-friend-items');
    items.forEach(function (item, index) {
      var row = document.createElement('label');
      row.setAttribute('style', 'display:flex;align-items:center;gap:12px;cursor:pointer;');
      var imgHtml = item.option.image
        ? '<img src="' + item.option.image + '" alt="" style="width:53px;height:53px;object-fit:contain;border-radius:6px;background:#f6f7f7;flex-shrink:0;">'
        : '<div style="width:53px;height:53px;border-radius:6px;background:#f6f7f7;flex-shrink:0;"></div>';
      row.innerHTML =
        imgHtml +
        '<span style="flex:1;min-width:0;">' +
          '<span style="display:block;font-size:1.05em;font-weight:600;overflow-wrap:break-word;">' + escapeHtml(item.option.label) + '</span>' +
          (item.option.price ? '<span style="display:block;font-size:0.95em;opacity:0.7;">' + escapeHtml(item.option.price) + '</span>' : '') +
        '</span>' +
        '<span style="display:flex;align-items:center;gap:5px;font-size:0.92em;white-space:nowrap;flex-shrink:0;">' +
          '<input type="checkbox" class="ask-a-friend-remove-checkbox" data-index="' + index + '" style="margin:0;">' + escapeHtml(t.removeLabel) +
        '</span>';
      itemsEl.appendChild(row);
    });

    var errorEl = box.querySelector('.ask-a-friend-error');
    var emailInput = box.querySelector('.ask-a-friend-email-input');
    var showPricesCheckbox = box.querySelector('.ask-a-friend-show-prices');

    function collectKeptOptions() {
      var kept = [];
      box.querySelectorAll('.ask-a-friend-remove-checkbox').forEach(function (cb) {
        var idx = parseInt(cb.dataset.index, 10);
        if (!cb.checked) kept.push(items[idx].option);
      });
      return kept;
    }

    function submit(notifyEmail) {
      var kept = collectKeptOptions();
      if (kept.length === 0) {
        errorEl.textContent = t.keepOneMessage;
        errorEl.style.display = 'block';
        return;
      }

      var showPrices = showPricesCheckbox.checked;
      var finalOptions = kept.map(function (option) {
        return showPrices ? option : Object.assign({}, option, { price: '' });
      });

      onDone(finalOptions, notifyEmail);
    }

    box.querySelector('.ask-a-friend-submit').addEventListener('click', function () {
      submit(emailInput.value.trim());
    });
    emailInput.addEventListener('keydown', function (evt) {
      if (evt.key === 'Enter') {
        evt.preventDefault();
        submit(emailInput.value.trim());
      }
    });

    emailInput.focus();
  }

  /**
   * Shows the poll's current tally in a large in-page modal (~90% of the
   * viewport, with a close "x" in the top-right corner), rather than
   * navigating to templates/page.ask-a-friend.liquid in a new tab. That
   * page has no header/nav (it's `{% layout none %}`, by design - so a
   * friend who just opens a shared link isn't dropped into a chromeless
   * page they can't navigate out of via the site itself), which meant a
   * shopper clicking "View results" right after creating their own poll
   * had no obvious way back to the shop except closing the tab. This
   * fetches the same tally data the results page uses and renders it
   * in place instead, matching the review/ready panels' modal style.
   * @param {string} pollId
   * @param {Array} options - [{label, price, image}]
   * @param {Object} t - strings()
   */
  function openResultsPanel(pollId, options, t) {
    var overlay = document.createElement('div');
    overlay.className = 'ask-a-friend-overlay ask-a-friend-results-overlay';
    overlay.setAttribute(
      'style',
      'position:fixed;inset:0;background:rgba(0,0,0,0.5);z-index:100000;display:flex;align-items:center;justify-content:center;padding:16px;'
    );

    var box = document.createElement('div');
    box.setAttribute(
      'style',
      'position:relative;background:#fff;color:#222;border-radius:8px;width:90vw;height:90vh;max-width:820px;' +
        'padding:36px 30px 28px;font-family:inherit;box-shadow:0 10px 40px rgba(0,0,0,0.25);overflow-y:auto;box-sizing:border-box;'
    );

    var closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'ask-a-friend-results-close';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.innerHTML = '&times;';
    closeBtn.setAttribute(
      'style',
      'position:absolute;top:10px;right:12px;width:40px;height:40px;border:none;background:transparent;' +
        'font-size:30px;line-height:1;cursor:pointer;color:#777;padding:0;'
    );

    function close() {
      overlay.remove();
      document.removeEventListener('keydown', onKeydown);
    }
    function onKeydown(evt) {
      if (evt.key === 'Escape') close();
    }
    closeBtn.addEventListener('click', close);
    overlay.addEventListener('click', function (evt) {
      if (evt.target === overlay) close();
    });
    document.addEventListener('keydown', onKeydown);

    var listHtml = '<div class="ask-a-friend-results-list" style="display:flex;flex-direction:column;gap:14px;"></div>';

    box.innerHTML =
      '<h3 style="margin:0 0 8px;font-size:1.3em;line-height:1.3;">' + escapeHtml(t.resultsTitle) + '</h3>' +
      '<p class="ask-a-friend-results-note" style="margin:0 0 20px;font-size:0.95em;opacity:0.7;">' + escapeHtml(t.loading) + '</p>' +
      listHtml;
    box.appendChild(closeBtn);

    var noteEl = box.querySelector('.ask-a-friend-results-note');
    var listEl = box.querySelector('.ask-a-friend-results-list');

    options.forEach(function (option, index) {
      var imgHtml = option.image
        ? '<img src="' + option.image + '" alt="" style="width:64px;height:64px;object-fit:contain;border-radius:8px;background:#f6f7f7;flex-shrink:0;">'
        : '<div style="width:64px;height:64px;border-radius:8px;background:#f6f7f7;flex-shrink:0;"></div>';

      var row = document.createElement('div');
      row.className = 'ask-a-friend-results-row';
      row.dataset.index = index;
      row.setAttribute('style', 'display:flex;align-items:center;gap:14px;background:#fff;border:1px solid #e5e5e5;border-radius:10px;padding:14px;');
      row.innerHTML =
        imgHtml +
        '<div style="flex:1;min-width:0;">' +
          '<p style="margin:0 0 2px;font-weight:600;font-size:1em;overflow-wrap:break-word;">' + escapeHtml(option.label || '') + '</p>' +
          (option.price ? '<p style="margin:0 0 8px;font-size:0.9em;opacity:0.7;">' + escapeHtml(option.price) + '</p>' : '') +
          '<div class="ask-a-friend-results-bar-track" style="background:#f6f7f7;border-radius:6px;height:8px;overflow:hidden;margin-top:8px;">' +
            '<div class="ask-a-friend-results-bar-fill" style="background:#333;height:100%;width:0%;transition:width 0.4s ease;"></div>' +
          '</div>' +
          '<p class="ask-a-friend-results-bar-count" style="font-size:0.8em;opacity:0.65;margin:5px 0 0;">&nbsp;</p>' +
        '</div>';
      listEl.appendChild(row);
    });

    overlay.appendChild(box);
    document.body.appendChild(overlay);
    closeBtn.focus();

    var settings = window.askAFriendSettings || {};
    if (!settings.endpoint) {
      noteEl.textContent = t.resultsLoadErrorNote;
      return;
    }

    fetch(settings.endpoint + '?action=tally&pollId=' + encodeURIComponent(pollId))
      .then(function (res) { return res.json(); })
      .then(function (result) {
        var counts = (result && result.counts) || [];
        var total = counts.reduce(function (sum, c) { return sum + (c || 0); }, 0);

        listEl.querySelectorAll('.ask-a-friend-results-row').forEach(function (row) {
          var i = parseInt(row.dataset.index, 10);
          var count = counts[i] || 0;
          var pct = total > 0 ? Math.round((count / total) * 100) : 0;
          row.querySelector('.ask-a-friend-results-bar-fill').style.width = pct + '%';
          row.querySelector('.ask-a-friend-results-bar-count').textContent =
            count + (count === 1 ? ' vote' : ' votes') + ' (' + pct + '%)';
        });

        noteEl.textContent = total > 0
          ? "Here's how your poll is doing so far."
          : t.resultsEmptyNote;
      })
      .catch(function () {
        noteEl.textContent = t.resultsLoadErrorNote;
      });
  }

  function renderReadyPanel(box, pollId, shareUrl, creatorUrl, options, t) {
    var previewHtml =
      '<div class="ask-a-friend-preview" style="display:flex;flex-direction:column;gap:12px;margin-bottom:22px;">' +
        '<p style="margin:0;font-size:0.98em;font-weight:600;opacity:0.7;">' + escapeHtml(t.previewLabel) + '</p>';

    options.forEach(function (option) {
      var imgHtml = option.image
        ? '<img src="' + option.image + '" alt="" style="width:56px;height:56px;object-fit:contain;border-radius:8px;background:#f6f7f7;flex-shrink:0;">'
        : '<div style="width:56px;height:56px;border-radius:8px;background:#f6f7f7;flex-shrink:0;"></div>';

      previewHtml +=
        '<div style="display:flex;align-items:center;gap:12px;background:#fff;border:1px solid #e5e5e5;border-radius:10px;padding:12px;">' +
          imgHtml +
          '<span style="flex:1;min-width:0;">' +
            '<span style="display:block;font-weight:600;font-size:1em;overflow-wrap:break-word;">' + escapeHtml(option.label) + '</span>' +
            (option.price ? '<span style="display:block;font-size:0.9em;opacity:0.7;">' + escapeHtml(option.price) + '</span>' : '') +
          '</span>' +
          '<button type="button" disabled style="flex-shrink:0;background:#333;color:#fff;border:none;border-radius:20px;padding:8px 16px;font-size:0.88em;opacity:0.5;cursor:default;">' + escapeHtml(t.voteButtonLabel) + '</button>' +
        '</div>';
    });

    previewHtml += '</div>';

    box.innerHTML =
      '<h3 style="margin:0 0 18px;font-size:1.3em;line-height:1.3;">' + escapeHtml(t.readyTitle) + '</h3>' +
      previewHtml +
      '<div class="ask-a-friend-share" style="display:flex;gap:8px;flex-wrap:wrap;margin-bottom:8px;">' +
        '<button type="button" class="btn btn--secondary ask-a-friend-copy" style="flex:1;min-width:120px;">' + escapeHtml(t.copyLink) + '</button>' +
        '<a class="btn btn--secondary ask-a-friend-email-share" style="flex:1;min-width:120px;text-align:center;text-decoration:none;">' + escapeHtml(t.emailShare) + '</a>' +
        '<a class="btn btn--secondary ask-a-friend-text-share" style="flex:1;min-width:120px;text-align:center;text-decoration:none;">' + escapeHtml(t.textShare) + '</a>' +
      '</div>' +
      '<p style="margin:0 0 22px;font-size:0.92em;opacity:0.65;line-height:1.4;">' + escapeHtml(t.copyLinkNote) + '</p>' +
      '<div class="ask-a-friend-results-row" style="display:flex;gap:8px;flex-wrap:wrap;">' +
        '<button type="button" class="btn btn--primary ask-a-friend-view" style="flex:1;min-width:140px;">' + escapeHtml(t.viewPoll) + '</button>' +
        '<button type="button" class="btn btn--secondary ask-a-friend-copy-results" style="flex:1;min-width:140px;">' + escapeHtml(t.copyResultsLink) + '</button>' +
      '</div>' +
      '<p style="margin:8px 0 0;font-size:0.88em;opacity:0.6;text-align:center;line-height:1.4;">' + escapeHtml(t.checkResultsNote) + '</p>';

    var copyBtn = box.querySelector('.ask-a-friend-copy');
    var copyLabel = t.copyLink;
    copyBtn.addEventListener('click', function () {
      var done = function () {
        copyBtn.textContent = 'Copied!';
        setTimeout(function () { copyBtn.textContent = copyLabel; }, 1500);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(shareUrl).then(done).catch(function () {
          window.prompt('Copy this link:', shareUrl);
        });
      } else {
        window.prompt('Copy this link:', shareUrl);
      }
    });

    // These always point at the plain voting link (shareUrl), never the
    // "View results" link - a friend who's sent a link should always land
    // on the page that lets them vote, not the creator's read-only results.
    var emailBody = encodeURIComponent(shareUrl);
    box.querySelector('.ask-a-friend-email-share').href = 'mailto:?subject=' + encodeURIComponent('Which one should I get?') + '&body=' + emailBody;
    box.querySelector('.ask-a-friend-text-share').href = 'sms:?&body=' + emailBody;

    // "View results" opens the tally in a large in-page modal (see
    // openResultsPanel) instead of navigating away, so there's always an
    // obvious way back (the "x" in the corner, a click on the backdrop,
    // or Escape) - see openResultsPanel's comment for why.
    box.querySelector('.ask-a-friend-view').addEventListener('click', function () {
      openResultsPanel(pollId, options, t);
    });

    // A shopper who wants to check back later needs the results link
    // itself in hand (to paste into notes, text themselves, etc.) - not
    // just a button that navigates away and is easy to lose track of.
    var copyResultsBtn = box.querySelector('.ask-a-friend-copy-results');
    var copyResultsLabel = t.copyResultsLink;
    copyResultsBtn.addEventListener('click', function () {
      var done = function () {
        copyResultsBtn.textContent = 'Copied!';
        setTimeout(function () { copyResultsBtn.textContent = copyResultsLabel; }, 1500);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(creatorUrl).then(done).catch(function () {
          window.prompt('Copy this link:', creatorUrl);
        });
      } else {
        window.prompt('Copy this link:', creatorUrl);
      }
    });
  }

  /**
   * Fetches each product's title, price and image (via Shopify's product
   * JSON endpoint) and maps them into Ask a Friend poll options, in the
   * order given. Shared by the compare drawer and the single-product
   * "Ask a Friend" button so both build poll options the same way.
   * @param {Array} products - array of {id, url} product objects
   * @returns {Promise<Array>} array of {label, price, image} option objects,
   *                           skipping any product that fails to load.
   */
  async function fetchOptions(products) {
    var options = new Array(products.length);

    await Promise.all(
      products.map(async function (product, index) {
        try {
          var response = await fetch(product.url + '.js');
          if (!response.ok) return;
          var data = await response.json();

          var sourceImage = data.featured_image || (data.images && data.images[0]) || '';
          if (sourceImage) {
            if (sourceImage.indexOf('//') === 0) sourceImage = 'https:' + sourceImage;
            sourceImage += sourceImage.indexOf('?') > -1 ? '&width=300' : '?width=300';
          }

          options[index] = {
            label: data.title,
            price: '$' + (data.price / 100).toFixed(2),
            image: sourceImage
          };
        } catch (e) {
          // Skip products that fail to load
        }
      })
    );

    return options.filter(Boolean);
  }

  /**
   * Shows the review step (remove items, toggle prices, optional email),
   * then swaps the same modal to a "ready to share" panel once the poll
   * link is built. No new tab is ever opened, so this can't be blocked by
   * a popup blocker.
   * @param {Object} pollData - {question, storeName, options: [{label, price, image}]}
   */
  function launch(pollData) {
    if (!pollData || !pollData.options || pollData.options.length === 0) return;

    var t = strings();
    var built = buildOverlay();
    var overlay = built.overlay;
    var box = built.box;
    document.body.appendChild(overlay);

    renderReviewPanel(box, pollData, t, function (finalOptions, notifyEmail) {
      var id = generatePollId();
      var data = {
        id: id,
        notifyEmail: notifyEmail || '',
        question: pollData.question,
        storeName: pollData.storeName,
        options: finalOptions
      };
      var b64 = encodePrefill(data);

      // Two flavors of the same link: the plain one goes to friends and
      // always shows the voting screen; the "results" one is only ever
      // meant for the shopper who created the poll, and tells the poll
      // page to show a read-only tally instead. `aaf_view` (not `view` -
      // see the file header note) is used so this never collides with
      // Shopify's own reserved `view` query parameter.
      var shareUrl = window.location.origin + '/pages/ask-a-friend?prefill=' + encodeURIComponent(b64);
      var creatorUrl = shareUrl + '&aaf_view=results';

      renderReadyPanel(box, id, shareUrl, creatorUrl, finalOptions, t);

      logEvent({
        action: 'created',
        pollId: id,
        storeName: pollData.storeName,
        itemCount: finalOptions.length,
        notifyEmail: notifyEmail || ''
      });
    });
  }

  window.AskAFriend = { launch: launch, fetchOptions: fetchOptions };
})();
